import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { mfaApi } from '../../api/mfa';
import './MFAVerificationForm.css';

interface MFAVerificationFormProps {
  sessionId: string;
  userInfo: {
    email: string;
    firstName?: string;
    lastName?: string;
  };
  onCancel: () => void;
}

const MFAVerificationForm: React.FC<MFAVerificationFormProps> = ({ 
  sessionId, 
  userInfo, 
  onCancel 
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code || (useBackupCode ? code.length < 8 : code.length !== 6)) {
      setError('Please enter a valid code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await mfaApi.post('/auth/mfa/verify', {
        session_id: sessionId,
        code: code.replace(/\s/g, '')
      });

      const { token, roles, email, user_id, warning, remaining_backup_codes } = response.data as any;
      
      // Store auth data
      localStorage.setItem('token', token);
      
      // Update auth context (we'll need to modify login to accept token directly)
      // For now, we'll reload the page to trigger the auth check
      window.location.href = '/';
      
      // Show warning if backup code was used
      if (warning === 'backup_code_used') {
        alert(`Backup code used. You have ${remaining_backup_codes} backup codes remaining.`);
      }

    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Invalid code. Please try again.';
      setError(errorMessage);
      
      if (errorMessage.includes('expired')) {
        setTimeout(() => onCancel(), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mfa-verification-container">
      <div className="mfa-verification-form">
        <h2>Two-Factor Authentication</h2>
        <p className="greeting">Hi {userInfo.firstName || userInfo.email.split('@')[0]}</p>
        <p className="instructions">
          Please enter your {useBackupCode ? 'backup' : 'authentication'} code to continue.
        </p>

        <form onSubmit={handleVerify}>
          <div className="form-group">
            <input
              type="text"
              className="code-input"
              placeholder={useBackupCode ? "XXXX-XXXX" : "000000"}
              value={code}
              onChange={(e) => {
                if (useBackupCode) {
                  setCode(e.target.value.toUpperCase());
                } else {
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                }
              }}
              autoFocus
              maxLength={useBackupCode ? 9 : 6}
            />
          </div>

          {error && (
            <div className="error-alert">{error}</div>
          )}

          <div className="button-group">
            <button 
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="btn btn-primary"
              disabled={loading || !code}
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </div>

          <div className="alternative-options">
            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setUseBackupCode(!useBackupCode);
                setCode('');
                setError('');
              }}
            >
              {useBackupCode ? 'Use authenticator app' : 'Use backup code'}
            </a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MFAVerificationForm;